/**
 * ErrorBoundary — app-root crash guard.
 *
 * Telegram Mini Apps render inside an embedded webview where an uncaught render error
 * leaves a blank white screen with no diagnostics. This boundary catches any render-time
 * error in the tree below it and shows a styled fallback (message + collapsible stack)
 * plus recovery actions ("На главную" / "Обновить"), so a crash is always visible and
 * never a silent white screen.
 */
import { Component, type ErrorInfo, type ReactNode } from "react";

import styles from "./ErrorBoundary.module.css";

export interface ErrorBoundaryProps {
  readonly children: ReactNode;
}

interface ErrorBoundaryState {
  readonly error: Error | null;
  readonly info: ErrorInfo | null;
}

export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  public override state: ErrorBoundaryState = { error: null, info: null };

  public static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error, info: null };
  }

  public override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Surface the full diagnostics in the console for debugging in Telegram webviews.
    // eslint-disable-next-line no-console
    console.error("Mini App render error:", error, info);
    this.setState({ error, info });
  }

  private readonly handleHome = (): void => {
    window.location.hash = "/";
    window.location.reload();
  };

  private readonly handleReload = (): void => {
    window.location.reload();
  };

  public override render(): ReactNode {
    const { error, info } = this.state;
    if (error === null) {
      return this.props.children;
    }

    const stack = [error.stack, info?.componentStack]
      .filter((part): part is string => Boolean(part))
      .join("\n\n");

    return (
      <div className={styles.root} role="alert">
        <div className={styles.card}>
          <span className={styles.glyph} aria-hidden="true">
            ✦
          </span>
          <h1 className={styles.title}>Что-то пошло не так</h1>
          <p className={styles.message}>
            {error.message || "Произошла непредвиденная ошибка."}
          </p>

          {stack ? (
            <details className={styles.details}>
              <summary className={styles.summary}>Подробности</summary>
              <pre className={styles.stack}>{stack}</pre>
            </details>
          ) : null}

          <div className={styles.actions}>
            <button
              type="button"
              className={styles.primary}
              onClick={this.handleHome}
            >
              На главную
            </button>
            <button
              type="button"
              className={styles.secondary}
              onClick={this.handleReload}
            >
              Обновить
            </button>
          </div>
        </div>
      </div>
    );
  }
}
